import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { searchBitrixDeals, BitrixDeal } from "../api/bitrix";
import { colors, spacing, borderRadius, typography, shadows, createButtonStyle } from "../styles/theme";

type Props = {
  onSelect: (deal: BitrixDeal) => void;
  onClear?: () => void;
  currentDeal?: BitrixDeal | null;
  placeholder?: string;
};

/**
 * Format budget as Polish currency
 */
const formatBudget = (budget: number | null): string => {
  if (budget === null || budget === undefined) return "-";
  return budget.toLocaleString("pl-PL", { style: "currency", currency: "PLN" });
};

/**
 * Dropdown search component for Bitrix deals
 * Uses Portal to render dropdown at body level for proper z-index stacking
 */
export default function BitrixDealSearch({
  onSelect,
  onClear,
  currentDeal,
  placeholder = "Szukaj deal (min. 2 znaki)...",
}: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<BitrixDeal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; openUp: boolean }>({
    top: 0,
    left: 0,
    width: 0,
    openUp: false,
  });

  const debounceRef = useRef<number | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position based on input element
  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return;

    const rect = inputRef.current.getBoundingClientRect();
    const dropdownHeight = 260; // Max height + margin
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Open upward if not enough space below and more space above
    const openUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    // For position: fixed, use viewport-relative coordinates (no scroll offset needed)
    setDropdownPosition({
      top: openUp ? rect.top : rect.bottom,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  }, []);

  // Update position when dropdown shows or window resizes/scrolls
  useEffect(() => {
    if (showDropdown) {
      updateDropdownPosition();

      const handleUpdate = () => updateDropdownPosition();
      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);

      return () => {
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
      };
    }
  }, [showDropdown, updateDropdownPosition]);

  // Debounced search effect
  useEffect(() => {
    setError(null);

    // Clear results if query too short
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    // Debounce 300ms
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const deals = await searchBitrixDeals(query);
        setResults(deals.slice(0, 10)); // Max 10 results
        setShowDropdown(true);
      } catch (err) {
        setError("Blad wyszukiwania dealow Bitrix");
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleSelect = (deal: BitrixDeal) => {
    onSelect(deal);
    setQuery("");
    setResults([]);
    setShowDropdown(false);
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
    }
    setQuery("");
    setResults([]);
  };

  // Render dropdown content (used in portal)
  const renderDropdownContent = () => {
    if (!showDropdown) return null;

    const hasResults = results.length > 0;
    const showNoResults = !loading && query.trim().length >= 2 && results.length === 0;

    if (!hasResults && !showNoResults) return null;

    const dropdownStyle: React.CSSProperties = {
      position: "fixed",
      left: dropdownPosition.left,
      width: dropdownPosition.width,
      background: colors.background,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.sm,
      boxShadow: shadows.lg,
      zIndex: 999999,
      maxHeight: 250,
      ...(dropdownPosition.openUp
        ? { bottom: window.innerHeight - dropdownPosition.top + spacing.xs }
        : { top: dropdownPosition.top + spacing.xs }),
    };

    if (hasResults) {
      return createPortal(
        <ul
          style={{
            ...dropdownStyle,
            margin: 0,
            padding: 0,
            listStyle: "none",
            maxHeight: 250,
            overflowY: "auto",
          }}
        >
          {results.map((deal) => (
            <li
              key={deal.id}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent input blur before click registers
                handleSelect(deal);
              }}
              style={{
                padding: spacing.md,
                cursor: "pointer",
                borderBottom: `1px solid ${colors.borderLight}`,
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.primaryLight;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <div
                style={{
                  fontWeight: typography.fontWeight.semibold,
                  marginBottom: spacing.xs,
                }}
              >
                {deal.title}
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                  display: "flex",
                  gap: spacing.md,
                }}
              >
                <span>Etap: {deal.stage_name}</span>
                <span>Budzet: {formatBudget(deal.budget)}</span>
              </div>
            </li>
          ))}
        </ul>,
        document.body
      );
    }

    if (showNoResults) {
      return createPortal(
        <div
          style={{
            ...dropdownStyle,
            padding: spacing.md,
            background: colors.gray50,
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
            textAlign: "center",
          }}
        >
          Brak wynikow dla &quot;{query}&quot;
        </div>,
        document.body
      );
    }

    return null;
  };

  // If showing current deal assignment
  if (currentDeal && !showDropdown) {
    return (
      <div
        ref={containerRef}
        style={{
          display: "flex",
          alignItems: "center",
          gap: spacing.sm,
          padding: spacing.sm,
          background: colors.primaryLight,
          border: `1px solid ${colors.primary}`,
          borderRadius: borderRadius.sm,
        }}
      >
        <span style={{ flex: 1, fontSize: typography.fontSize.sm }}>
          <strong>Przypisany:</strong> {currentDeal.title}
          <span style={{ color: colors.textSecondary, marginLeft: spacing.sm }}>
            ({currentDeal.stage_name})
          </span>
        </span>
        <button
          onClick={() => setShowDropdown(true)}
          style={{
            ...createButtonStyle("secondary"),
            padding: `${spacing.xs}px ${spacing.sm}px`,
            fontSize: typography.fontSize.xs,
          }}
        >
          Zmien
        </button>
        {onClear && (
          <button
            onClick={handleClear}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: colors.error,
              fontSize: typography.fontSize.lg,
              padding: spacing.xs,
              lineHeight: 1,
            }}
            title="Usun przypisanie"
          >
            x
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", maxWidth: "100%" }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => {
          updateDropdownPosition();
          if (results.length > 0) setShowDropdown(true);
        }}
        onBlur={() => {
          // Delay hiding to allow click on results
          setTimeout(() => setShowDropdown(false), 200);
        }}
        placeholder={placeholder}
        style={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          padding: `${spacing.sm}px ${spacing.md}px`,
          border: `1px solid ${colors.border}`,
          borderRadius: borderRadius.sm,
          fontSize: typography.fontSize.base,
        }}
      />

      {loading && (
        <div
          style={{
            position: "absolute",
            right: spacing.md,
            top: "50%",
            transform: "translateY(-50%)",
            color: colors.textSecondary,
            fontSize: typography.fontSize.sm,
          }}
        >
          ...
        </div>
      )}

      {error && (
        <div
          style={{
            marginTop: spacing.xs,
            padding: spacing.sm,
            background: colors.errorLight,
            color: colors.errorDark,
            borderRadius: borderRadius.sm,
            fontSize: typography.fontSize.sm,
          }}
        >
          {error}
        </div>
      )}

      {/* Dropdown rendered via portal */}
      {renderDropdownContent()}

      {currentDeal && showDropdown && (
        <button
          onClick={() => setShowDropdown(false)}
          style={{
            position: "absolute",
            top: spacing.xs,
            right: spacing.xs,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: colors.textSecondary,
            fontSize: typography.fontSize.sm,
          }}
        >
          Anuluj
        </button>
      )}
    </div>
  );
}
